"use client";

import { RiCalendarLine, RiCloseLine } from "@remixicon/react";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { Button } from "@workspace/ui/components/button";
import { Calendar } from "@workspace/ui/components/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";

export type DatePreset = "tonight" | "this-weekend" | "this-week" | "this-month";

export interface DateFilterValue {
	preset: DatePreset | null;
	startDate: string | null;
	endDate: string | null;
}

export interface ShowDateFilterProps {
	startDate: string | null;
	endDate: string | null;
	preset: DatePreset | null;
	onChange: (filter: DateFilterValue) => void;
	className?: string;
}

const PRESET_LABELS: Record<DatePreset, string> = {
	"tonight": "Tonight",
	"this-weekend": "This Weekend",
	"this-week": "This Week",
	"this-month": "This Month",
};

function toDateString(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

function resolvePreset(preset: DatePreset): { startDate: string; endDate: string } {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

	switch (preset) {
		case "tonight":
			return { startDate: toDateString(today), endDate: toDateString(today) };

		case "this-weekend": {
			let friday: Date;
			let sunday: Date;

			if (dayOfWeek === 0) {
				// Sunday — show today only
				friday = new Date(today);
				sunday = new Date(today);
			} else if (dayOfWeek >= 5) {
				// Friday or Saturday — current Fri-Sun
				friday = new Date(today);
				friday.setDate(today.getDate() - (dayOfWeek - 5));
				sunday = new Date(friday);
				sunday.setDate(friday.getDate() + 2);
			} else {
				// Mon-Thu — upcoming Fri-Sun
				friday = new Date(today);
				friday.setDate(today.getDate() + (5 - dayOfWeek));
				sunday = new Date(friday);
				sunday.setDate(friday.getDate() + 2);
			}
			return { startDate: toDateString(friday), endDate: toDateString(sunday) };
		}

		case "this-week": {
			// Today through next Sunday
			const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
			const nextSunday = new Date(today);
			nextSunday.setDate(today.getDate() + daysUntilSunday);
			return { startDate: toDateString(today), endDate: toDateString(nextSunday) };
		}

		case "this-month": {
			const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
			const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
			return { startDate: toDateString(firstDay), endDate: toDateString(lastDay) };
		}
	}
}

function getDisplayLabel(
	preset: DatePreset | null,
	startDate: string | null,
	endDate: string | null,
): string {
	if (preset) return PRESET_LABELS[preset];
	if (startDate && endDate) {
		const start = new Date(startDate + "T00:00:00");
		const end = new Date(endDate + "T00:00:00");
		if (startDate === endDate) {
			return format(start, "MMM d");
		}
		if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
			return `${format(start, "MMM d")} – ${format(end, "d")}`;
		}
		return `${format(start, "MMM d")} – ${format(end, "MMM d")}`;
	}
	return "All Dates";
}

export function ShowDateFilter({
	startDate,
	endDate,
	preset,
	onChange,
	className,
}: ShowDateFilterProps) {
	const [open, setOpen] = useState(false);

	const isActive = startDate !== null || endDate !== null;
	const displayLabel = getDisplayLabel(preset, startDate, endDate);

	// Convert string dates to Date objects for the Calendar
	const calendarRange: DateRange | undefined =
		startDate && endDate
			? {
					from: new Date(startDate + "T00:00:00"),
					to: new Date(endDate + "T00:00:00"),
				}
			: undefined;

	const handlePresetClick = useCallback(
		(p: DatePreset) => {
			const scrollPosition = window.scrollY;
			const dates = resolvePreset(p);
			onChange({ preset: p, ...dates });
			setOpen(false);
			requestAnimationFrame(() => {
				window.scrollTo(0, scrollPosition);
			});
		},
		[onChange],
	);

	const handleCalendarSelect = useCallback(
		(range: DateRange | undefined) => {
			if (range?.from) {
				const start = toDateString(range.from);
				const end = range.to ? toDateString(range.to) : start;
				onChange({ preset: null, startDate: start, endDate: end });
			}
		},
		[onChange],
	);

	const handleClear = useCallback(
		(e?: React.MouseEvent) => {
			e?.stopPropagation();
			const scrollPosition = window.scrollY;
			onChange({ preset: null, startDate: null, endDate: null });
			setOpen(false);
			requestAnimationFrame(() => {
				window.scrollTo(0, scrollPosition);
			});
		},
		[onChange],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn("h-10 w-full justify-between px-3", className)}
				>
					<div className="flex min-w-0 items-center gap-2">
						<RiCalendarLine className="h-4 w-4 shrink-0" />
						<span className="truncate">{displayLabel}</span>
					</div>
					{isActive ? (
						<div
							role="button"
							tabIndex={0}
							onClick={handleClear}
							onKeyDown={(e) => { if (e.key === "Enter") handleClear(e as any); }}
							className="ml-1 shrink-0 cursor-pointer rounded-full p-0.5 hover:bg-muted"
							aria-label="Clear date filter"
						>
							<RiCloseLine className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
						</div>
					) : (
						<div className="ml-2 h-4 w-4 shrink-0" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-auto min-w-[280px] p-0"
				side="bottom"
				align="start"
				sideOffset={4}
				collisionPadding={8}
			>
				<div className="p-3 space-y-3">
					{/* Preset buttons */}
					<div className="grid grid-cols-2 gap-2">
						{(Object.entries(PRESET_LABELS) as [DatePreset, string][]).map(
							([key, label]) => (
								<Button
									key={key}
									variant={preset === key ? "default" : "outline"}
									size="sm"
									className="h-8 text-xs"
									onClick={() => handlePresetClick(key)}
								>
									{label}
								</Button>
							),
						)}
					</div>

					{/* Calendar */}
					<Calendar
						mode="range"
						selected={calendarRange}
						onSelect={handleCalendarSelect}
						numberOfMonths={1}
						className="p-2 rounded-lg border"
					/>

					{/* Clear button */}
					{isActive && (
						<Button
							variant="outline"
							size="sm"
							className="w-full h-8 text-xs text-destructive hover:text-destructive"
							onClick={() => handleClear()}
						>
							Clear Dates
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
